import { useEffect, useRef, useState } from "react";
import { orgColor, orgInitials } from "@/features/identity-hub/utils/iam.utils";
import { ORG_ACCENT_FRAME_BORDER_CLASS, orgAccentVar } from "@/features/identity-hub/utils/org-accent-surface";
import { cn } from "@nexus/ui";
import { SuperTenantOrgAvatar } from "./SuperTenantOrgAvatar";

interface OrgLogoMarkProps {
  name: string;
  logoUrl?: string | null;
  accent?: string | null;
  isSuperTenant?: boolean;
  /** Drop all derived tenant colors — neutral frame + muted fallback avatar. */
  neutral?: boolean;
  size?: number;
  textClassName?: string;
  className?: string;
}

type LogoStatus = "loading" | "loaded" | "error";

/**
 * Meaningful placeholder shown immediately (colored initials / supertenant /
 * neutral). Also acts as the base layer beneath a logo image so there is never
 * a blank flash while the image loads, and a stable fallback if it fails.
 */
function FallbackMark({
  name,
  color,
  isSuperTenant,
  neutral,
  size,
  radiusClass,
  textClassName,
  className,
}: {
  name: string;
  color: string;
  isSuperTenant: boolean;
  neutral: boolean;
  size: number;
  radiusClass: string;
  textClassName?: string;
  className?: string;
}) {
  if (isSuperTenant) {
    return <SuperTenantOrgAvatar name={name} size={size} textClassName={textClassName} className={className} />;
  }

  if (neutral) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center border border-border bg-muted font-bold text-muted-foreground",
          radiusClass,
          className,
        )}
        style={{ width: size, height: size, fontSize: size * 0.42 }}
      >
        {orgInitials(name)}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-bold text-white ring-1 ring-black/10",
        radiusClass,
        className,
      )}
      style={{ background: color, width: size, height: size, fontSize: size * 0.42 }}
    >
      {orgInitials(name)}
    </span>
  );
}

export function OrgLogoMark({
  name,
  logoUrl,
  accent,
  isSuperTenant = false,
  neutral = false,
  size = 36,
  textClassName,
  className,
}: OrgLogoMarkProps) {
  const color = accent ?? orgColor(name);
  const radiusClass = size <= 24 ? "rounded" : "rounded-lg";
  const [status, setStatus] = useState<LogoStatus>("loading");
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset load state when the source changes so a new logo shows its own
  // placeholder/fade instead of inheriting the previous image's state. Cached
  // images can finish before React attaches onLoad, so reconcile from the DOM.
  useEffect(() => {
    setStatus("loading");
    const img = imgRef.current;
    if (img?.complete) {
      setStatus(img.naturalWidth > 0 ? "loaded" : "error");
    }
  }, [logoUrl]);

  if (!logoUrl || status === "error") {
    return (
      <FallbackMark
        name={name}
        color={color}
        isSuperTenant={isSuperTenant}
        neutral={neutral}
        size={size}
        radiusClass={radiusClass}
        textClassName={textClassName}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden border",
        radiusClass,
        neutral ? "border-border" : ORG_ACCENT_FRAME_BORDER_CLASS,
        className,
      )}
      style={neutral ? { width: size, height: size } : { ...orgAccentVar(color), width: size, height: size }}
    >
      <div
        className={cn(
          "absolute inset-0 flex items-center justify-center",
          status === "loading" && "motion-safe:animate-pulse",
        )}
      >
        <FallbackMark
          name={name}
          color={color}
          isSuperTenant={isSuperTenant}
          neutral={neutral}
          size={size}
          radiusClass={radiusClass}
          textClassName={textClassName}
        />
      </div>
      <img
        ref={imgRef}
        src={logoUrl}
        alt={name}
        loading="lazy"
        decoding="async"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        className={cn(
          "relative size-full object-cover transition-opacity duration-300 ease-out motion-reduce:transition-none",
          status === "loaded" ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}
